import { Autocomplete, Box, TextField, Typography, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';

import { useConceptAutocomplete } from './useConceptAutoComplete';
import { AddedConcept } from '../../state/createProjectSlice';
import { ConceptGroupResponse } from '../../types/response';

interface ConceptAutocompleteProps {
  concept: AddedConcept;
}
export const ConceptAutocomplete = ({ concept }: ConceptAutocompleteProps) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const {
    conceptGroups,
    isConceptGroupsLoading,
    handleChange,
    inputVal,
    setInputVal,
    addedConceptNames,
    isUserEditing,
    setIsUserEditing,
  } = useConceptAutocomplete({ concept });
  return (
    <Box id="concept-autocomplete">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
        <Typography variant="body1">
          {formatMessage({
            id: 'createProjectWizard.step.conceptGroupsAndAttributes.selectConceptGroup.title',
          })}
        </Typography>
        <Typography variant="body2" sx={{ color: 'error.main' }}>
          *
        </Typography>
      </Box>
      <Autocomplete
        options={conceptGroups}
        loading={isConceptGroupsLoading}
        inputValue={inputVal}
        onInputChange={(event, newInputValue, reason) => {
          if (reason === 'input') setIsUserEditing(true);
          setInputVal(newInputValue);
        }}
        filterOptions={(x) => x}
        getOptionLabel={(option) => option.label}
        onChange={(event, option) => {
          handleChange(event, option);
        }}
        value={
          isUserEditing
            ? null
            : conceptGroups.find((conceptGroup: ConceptGroupResponse) =>
                concept.conceptGroupId
                  ? conceptGroup.id === concept.conceptGroupId
                  : conceptGroup.label === concept.conceptGroupName
              ) ?? null
        }
        getOptionDisabled={(option) => addedConceptNames.includes(option.label)}
        renderOption={(props, option) => {
          const { key, ...otherProps } = props;
          const isAdded = addedConceptNames.includes(option.label);
          return (
            <Box
              key={key}
              component="li"
              {...otherProps}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                opacity: isAdded ? 0.6 : 1,
              }}
            >
              <span>{option.label}</span>

              {isAdded && (
                <Typography variant="caption" color="error" sx={{ ml: 2, fontWeight: 600 }}>
                  Added
                </Typography>
              )}
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={formatMessage({
              id: 'createProjectWizard.step.conceptGroupsAndAttributes.selectConceptGroup.placeholder',
            })}
          />
        )}
      />
      <Box>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          {formatMessage({
            id: 'createProjectWizard.step.conceptGroupsAndAttributes.selectConceptGroup.description',
          })}
        </Typography>
      </Box>
    </Box>
  );
};
